'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { useStore } from '@/store/store';
import { apiSetReminder, type ReminderChannel } from '@/logic/reminders/reminders';
import { buildReminderEvent, type ReminderModule } from '@/tracking/amplitude/events/general/reminder';
import { useGlobalContext } from '@/store/contexts/global-context';
import { getPlatformBridge } from '@/logic/bridges';

type NotificationPermissionEvent = CustomEvent<{ granted: boolean }>;

const PERMISSION_TIMEOUT_MS = 30_000;

type UseReminderResult = {
    isSet: boolean;
    isLoading: boolean;
    toggle: () => Promise<void>;
};

export const useReminder = (
    zoneUid: string,
    gameId: string,
    module: ReminderModule,
    gameName: string
): UseReminderResult => {
    const isSet = useStore((state) => state.reminders.remindersByZone[zoneUid] ?? false);
    const setReminder = useStore((state) => state.reminders.setReminder);
    const { sendEvent } = useStore((state) => state.tracking);
    const { isAndroidWrapper } = useGlobalContext();

    const [hasPermission, setHasPermission] = useState(() => {
        if (isAndroidWrapper) return getPlatformBridge().exec('hasNotificationPermission') ?? false;
        return true;
    });
    const pendingPermissionCleanupRef = useRef<(() => void) | null>(null);

    useEffect(() => {
        if (!isAndroidWrapper) return;

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                setHasPermission(getPlatformBridge().exec('hasNotificationPermission') ?? false);
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            pendingPermissionCleanupRef.current?.();
        };
    }, [isAndroidWrapper]);

    const requestAndroidPermission = useCallback((): Promise<boolean> => {
        pendingPermissionCleanupRef.current?.();

        const bridge = getPlatformBridge();

        return new Promise<boolean>((resolve) => {
            const handler = (event: Event) => {
                globalThis.removeEventListener('notificationPermissionResult', handler);
                // eslint-disable-next-line no-use-before-define -- mutual reference: handler is only called after timeoutId is assigned
                clearTimeout(timeoutId);
                pendingPermissionCleanupRef.current = null;
                const { granted } = (event as NotificationPermissionEvent).detail;
                setHasPermission(granted);
                resolve(granted);
            };

            const timeoutId = setTimeout(() => {
                globalThis.removeEventListener('notificationPermissionResult', handler);
                pendingPermissionCleanupRef.current = null;
                resolve(false);
            }, PERMISSION_TIMEOUT_MS);

            pendingPermissionCleanupRef.current = () => {
                globalThis.removeEventListener('notificationPermissionResult', handler);
                clearTimeout(timeoutId);
                pendingPermissionCleanupRef.current = null;
            };

            globalThis.addEventListener('notificationPermissionResult', handler);
            bridge.exec('requestNotificationPermission');
        });
    }, []);

    const [isLoading, setIsLoading] = useState(false);
    const pendingRef = useRef(false);

    const toggle = useCallback(async () => {
        if (pendingRef.current) return;

        setIsLoading(true);
        pendingRef.current = true;

        try {
            if (!isSet && !hasPermission) {
                const granted = await requestAndroidPermission();
                if (!granted) return;
            }

            const newState = !isSet;
            setReminder(zoneUid, newState);

            const channel: ReminderChannel = isAndroidWrapper ? 'android' : 'desktop';
            const apiSuccess = await apiSetReminder(gameId, newState, channel);

            if (apiSuccess) {
                sendEvent([
                    { destination: 'amplitude', eventProps: buildReminderEvent(newState, { module, gameName }) }
                ]);
            } else {
                setReminder(zoneUid, isSet);
                console.warn(`Failed to toggle reminder for zone ${zoneUid}`);
            }
        } catch (error) {
            setReminder(zoneUid, isSet);
            console.error('toggleReminder error:', error);
        } finally {
            pendingRef.current = false;
            setIsLoading(false);
        }
    }, [zoneUid, gameId, module, gameName, isSet, hasPermission, requestAndroidPermission, setReminder, sendEvent]);

    return { isSet, isLoading, toggle };
};
